import { queryOptions } from "@tanstack/react-query";
import { vendureCacheConfig } from "@/api/vendure/cache.config";
import { apiClient } from "@/lib/api-client";
import type {
  AttachCustomerPayload,
  EligiblePaymentMethodDto,
  FlushCartContext,
  FlushCartRequest,
  OrderDetailDto,
  OrderDto,
  PaginatedOrdersDto,
  PlaceOrderRequest,
  PlaceOrderResponse,
} from "./order.types";
import { PaymentPlatform } from "./order.types";

const cartHeaders = (channelToken: string) => ({
  "vendure-token": channelToken,
});

export async function activeCartFn(
  channelToken: string,
): Promise<OrderDto | null> {
  try {
    const { data } = await apiClient.get<OrderDto>(
      "/api/v1/munchi-cart/active",
      { headers: cartHeaders(channelToken) },
    );
    return data ?? null;
  } catch {
    return null;
  }
}

export const activeCartQueryOptions = (channelToken: string, enabled = true) =>
  queryOptions({
    queryKey: ["active-cart", channelToken],
    queryFn: () => activeCartFn(channelToken),
    enabled: enabled && !!channelToken,
    staleTime: 0,
  });

// A method with no platforms listed works everywhere; otherwise it must list the web.
const supportsWeb = (method: EligiblePaymentMethodDto): boolean => {
  const platforms = method.customFields?.supportedPlatforms ?? [];
  return platforms.length === 0 || platforms.includes(PaymentPlatform.Web);
};

export async function eligiblePaymentMethodsFn(
  channelToken: string,
): Promise<EligiblePaymentMethodDto[]> {
  const { data } = await apiClient.get<EligiblePaymentMethodDto[]>(
    "/api/v1/munchi-cart/eligible-payment-methods",
    { headers: cartHeaders(channelToken) },
  );
  return (data ?? []).filter(supportsWeb);
}

export async function setCustomerForCartFn(
  channelToken: string,
  payload: AttachCustomerPayload,
): Promise<{ cart: OrderDto }> {
  const res = await apiClient.patch("/api/v1/munchi-cart/customer", payload, {
    headers: cartHeaders(channelToken),
  });

  return { cart: res.data };
}

export const eligiblePaymentMethodsQueryOptions = (
  channelToken: string,
  enabled = true,
) =>
  queryOptions({
    queryKey: ["eligible-payment-methods", channelToken],
    queryFn: () => eligiblePaymentMethodsFn(channelToken),
    enabled: enabled && !!channelToken,
    ...vendureCacheConfig,
  });

export async function flushCartFn(
  channelToken: string,
  body: FlushCartRequest,
): Promise<{ cart: OrderDto }> {
  const res = await apiClient.post<OrderDto>(
    "/api/v1/munchi-cart/flush",
    body,
    { headers: cartHeaders(channelToken) },
  );
  return { cart: res.data };
}

export async function setCartContextFn(
  channelToken: string,
  context: FlushCartContext,
): Promise<void> {
  await apiClient.patch("/api/v1/munchi-cart/context", context, {
    headers: cartHeaders(channelToken),
  });
}

export async function applyCouponFn(
  channelToken: string,
  couponCode: string,
): Promise<OrderDto> {
  const { data } = await apiClient.post<OrderDto>(
    "/api/v1/munchi-cart/coupon",
    { couponCode },
    { headers: cartHeaders(channelToken) },
  );
  return data;
}

export async function removeCouponFn(
  channelToken: string,
  couponCode: string,
): Promise<OrderDto> {
  const { data } = await apiClient.delete<OrderDto>(
    `/api/v1/munchi-cart/coupon/${encodeURIComponent(couponCode)}`,
    { headers: cartHeaders(channelToken) },
  );
  return data;
}

export async function placeOrderFn(
  channelToken: string,
  body: PlaceOrderRequest,
): Promise<PlaceOrderResponse> {
  const { data } = await apiClient.post<PlaceOrderResponse>(
    "/api/v1/munchi-cart/place",
    body,
    { headers: cartHeaders(channelToken) },
  );
  return data;
}

export async function confirmOrderFn(
  channelToken: string,
  sessionId: string,
): Promise<PlaceOrderResponse> {
  const { data } = await apiClient.post<PlaceOrderResponse>(
    "/api/v1/munchi-cart/confirm",
    { sessionId },
    { headers: cartHeaders(channelToken) },
  );
  return data;
}

export async function orderDetailFn(code: string): Promise<OrderDetailDto> {
  const { data } = await apiClient.get<OrderDetailDto>(`/api/v1/order/${code}`);
  return data;
}

export const orderDetailQueryOptions = (code: string, enabled = true) =>
  queryOptions({
    queryKey: ["order-detail", code],
    queryFn: () => orderDetailFn(code),
    enabled: enabled && !!code,
    staleTime: 30_000,
  });

export async function orderHistoryFn(
  slug: string,
  page = 1,
  pageSize = 10,
): Promise<PaginatedOrdersDto> {
  const { data } = await apiClient.get<PaginatedOrdersDto>("/api/v1/orders", {
    params: { page, pageSize },
    headers: { "vendure-token": slug },
  });
  return data;
}

export const orderHistoryQueryOptions = (
  slug: string,
  page: number,
  enabled = true,
) =>
  queryOptions({
    queryKey: ["order-history", slug, page],
    queryFn: () => orderHistoryFn(slug, page),
    enabled: enabled && !!slug,
    staleTime: 60_000,
  });
